import React, {useState} from 'react';
import {SharedPageTrackingContextProvider} from '../../tracking/context/PageTrackingContext';
import TrackContentPageView from '../../tracking/trackerComponents/TrackContentPageView';
import SimpleButton from '../../ui/Buttons/SimpleButton';
import {CdnProductImage} from '../../ui/CdnImage/CdnImage';
import RadioButton from '../../ui/Forms/Inputs/RadioButton';
import TextArea from '../../ui/Forms/Inputs/TextArea';
import TextInput from '../../ui/Forms/Inputs/TextInput';
import {useNavigateClient} from '../../ui/Navigation/ClientSideLink/ClientSideLink';
import {ModalDialog} from '../../ui/utilityComponents/ModalDialog/ModalDialog';
import {useIsomorphicLayoutEffect} from '../../utils/hooks/useIsomorphicLayoutEffect';
import {useTimedOut} from '../../utils/hooks/useManualTimeout';
import {
  createOrderDetailUrl,
  ORDERS_OVERVIEW_ROUTE,
} from '../AccountArea/routes';
import AlreadyVotedMessage from './components/AlreadyVotedMessage';
import ErrorMessage from './components/ErrorMessage';
import HorizontalRatingGroup from './components/HorizontalRatingGroup';
import SuccessMessage from './components/SuccessMessage';
import {useProductReviewTranslations} from './context/ProductReviewTranslationsProvider';
import {useIovationFingerprint} from './hooks/useIovationFingerprint';
import useProductReviews from './hooks/useProductReviews';
import useSendProductReview from './hooks/useSendProductReview';
import {
  FormFields,
  FormRow,
  Panel,
  RatingTitle,
  REVIEW_IMAGE_WIDTH,
  StyledProductImageWrapper,
  StyledRadioGroup,
  StyledStarRatingButton,
  StyledTitle,
  SubmitButtonRow,
} from './ProductReview.styled';

const OrderDetailsProductRating: React.FC<
  React.PropsWithChildren<{
    displayName?: string;
    productId: number;
  }>
> = ({children, displayName, productId}) => {
  const {sizeRatings, qualityRatings, errorMessages, ...translations} =
    useProductReviewTranslations();
  const [modalStatus, setModalStatus] = useState<
    'success' | 'error' | 'alreadyVoted' | false
  >(false);
  const [rating, setRating] = useState('0');
  const {imageHash, isAllowedToSubmitReview, orderId} =
    useProductReviews(productId);

  const timedOut = useTimedOut(0);

  const {submitHandler, register, loadingState, errors, getValues} =
    useSendProductReview(
      productId,
      {displayName},
      (status: 'success' | 'error' | 'alreadyVoted') => {
        setModalStatus(status);
      },
    );
  const navigateClient = useNavigateClient();

  useIsomorphicLayoutEffect(() => {
    if (timedOut && !isAllowedToSubmitReview) {
      if (orderId) {
        navigateClient(createOrderDetailUrl(orderId))();
      } else {
        navigateClient(ORDERS_OVERVIEW_ROUTE)();
      }
    }
  }, [isAllowedToSubmitReview, navigateClient, orderId, timedOut]);

  return (
    <SharedPageTrackingContextProvider
      // @ts-ignore
      type={`productReviewSubmit|${productId}`}
    >
      <TrackContentPageView trackingCustomer={null}>
        <form onSubmit={submitHandler} data-testid={`review-form-${productId}`}>
          <FormFields>
            <RatingTitle data-testid={`review-fieldTitle-rating`}>
              {translations.ratingTitle}
            </RatingTitle>
            <FormRow>
              <Panel hasThumb>
                <StyledProductImageWrapper hasImage={!!imageHash}>
                  <CdnProductImage
                    padded
                    trim
                    data-testid="CdnImage"
                    loading="lazy"
                    width={REVIEW_IMAGE_WIDTH}
                    hash={`${imageHash}`}
                  />
                </StyledProductImageWrapper>
                <div>
                  <StyledTitle>{translations.starRatingTitle}*</StyledTitle>
                  <StyledStarRatingButton
                    name="rating"
                    ref={register}
                    onChange={event => {
                      setRating(event.target.value);
                    }}
                  />
                </div>
              </Panel>
            </FormRow>
            <FormRow>
              <Panel>
                <StyledTitle data-testid={`review-fieldTitle-fit`}>
                  {translations.sizeRatingTitle}
                </StyledTitle>
                <HorizontalRatingGroup
                  values={sizeRatings}
                  name="size"
                  ref={register}
                />
              </Panel>
            </FormRow>
            <FormRow>
              <Panel>
                <StyledTitle data-testid={`review-fieldTitle-quality`}>
                  {translations.qualityRatingTitle}
                </StyledTitle>
                <HorizontalRatingGroup
                  values={qualityRatings}
                  name="quality"
                  ref={register}
                />
              </Panel>
            </FormRow>
            <FormRow>
              <Panel>
                <StyledTitle data-testid={`review-fieldTitle-recommended`}>
                  {translations.recommendTitle}
                </StyledTitle>
                <StyledRadioGroup>
                  <RadioButton
                    label={translations.yesLabel}
                    name="recommended"
                    value="1"
                    ref={register}
                    data-testid="review-recommended-yes"
                  />
                  <RadioButton
                    label={translations.noLabel}
                    name="recommended"
                    value="0"
                    data-testid="review-recommended-no"
                    ref={register}
                  />
                </StyledRadioGroup>
              </Panel>
            </FormRow>
            <FormRow>{children}</FormRow>
            <FormRow>
              <TextInput
                value={getValues('displayName')}
                name="displayName"
                data-testid="review-displayName"
                placeholder={`${translations.displayNameLabel}*`}
                errorMessage={errors.displayName?.message}
                ref={register({
                  required: errorMessages.required,
                })}
              />
            </FormRow>
            <FormRow>
              <TextInput
                name="reviewTitle"
                data-testid="review-reviewTitle"
                placeholder={translations.reviewTitle}
                errorMessage={errors.reviewTitle?.message}
                ref={register({
                  validate: value => {
                    if (
                      value.length === 0 &&
                      getValues('reviewText').length > 0
                    ) {
                      return errorMessages.required;
                    }

                    return value.length < 100
                      ? true
                      : errorMessages.maxLength(100);
                  },
                })}
                value={getValues('reviewTitle')}
              />
            </FormRow>
            <FormRow>
              <TextArea
                name="reviewText"
                data-testid="review-reviewText"
                placeholder={translations.reviewText}
                errorMessage={errors.reviewText?.message}
                ref={register({
                  validate: value => {
                    if (
                      value.length === 0 &&
                      getValues('reviewTitle').length > 0
                    ) {
                      return errorMessages.required;
                    }

                    return value.length === 0 || value.length >= 50
                      ? true
                      : errorMessages.minLength(50);
                  },
                })}
                value={getValues('reviewText')}
              />
            </FormRow>
          </FormFields>
          <SubmitButton rating={rating} loadingState={loadingState} />
        </form>
        <ModalDialog
          showCloseIcon
          onClose={() => {
            if (orderId && modalStatus !== 'error') {
              navigateClient(createOrderDetailUrl(orderId))();
            } else {
              setModalStatus(false);
            }
          }}
          open={Boolean(modalStatus)}
          styles={{modal: {width: 313, top: 0, height: 'auto'}}}
        >
          {modalStatus === 'alreadyVoted' && <AlreadyVotedMessage />}
          {modalStatus === 'success' && <SuccessMessage />}
          {modalStatus === 'error' && <ErrorMessage />}
        </ModalDialog>
      </TrackContentPageView>
    </SharedPageTrackingContextProvider>
  );
};

const SubmitButton: React.FC<
  React.PropsWithChildren<{
    rating: string;
    loadingState: boolean;
  }>
> = ({rating, loadingState}) => {
  const {submitButtonText} = useProductReviewTranslations();
  const fingerprint = useIovationFingerprint();

  return (
    <SubmitButtonRow>
      <SimpleButton
        data-testid="review-submitButton"
        disabled={rating === '0' || !fingerprint}
        role="button"
        type="submit"
        loading={loadingState}
      >
        {submitButtonText}
      </SimpleButton>
    </SubmitButtonRow>
  );
};

export default OrderDetailsProductRating;
